/* Accessible, dependency-free storefront components. Safe under section replacement. */
(() => {
  'use strict';
  if (window.__todaysunnyUI) return;
  window.__todaysunnyUI = true;
  const strings = (key, fallback) => window.tsStrings?.[key] || fallback;
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!customElements.get('ts-carousel')) customElements.define('ts-carousel', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ts-track]');
      if (!this.track) return;
      this.abort = new AbortController(); const signal = this.abort.signal;
      this.prev = this.querySelector('[data-ts-prev]'); this.next = this.querySelector('[data-ts-next]');
      this.update = this.update.bind(this);
      this.track.addEventListener('scroll',this.update,{passive:true,signal});
      this.track.addEventListener('keydown',(event) => {
        if (event.target !== this.track || !['ArrowLeft','ArrowRight'].includes(event.key) || !this.isScrollable()) return;
        event.preventDefault(); this.move(event.key === 'ArrowRight' ? 1 : -1);
      },{signal});
      this.prev?.addEventListener('click',()=>this.move(-1),{signal});
      this.next?.addEventListener('click',()=>this.move(1),{signal});
      this.track.addEventListener('dragstart',(event)=>event.preventDefault(),{signal});
      this.track.addEventListener('pointerdown',(event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || !this.isScrollable() || event.target.closest('button,input,select,textarea,video,iframe')) return;
        this.drag = {id:event.pointerId,x:event.clientX,y:event.clientY,left:this.track.scrollLeft,moved:false};
      },{signal});
      this.track.addEventListener('pointermove',(event) => {
        if (!this.drag || this.drag.id !== event.pointerId) return;
        const dx = event.clientX-this.drag.x; const dy = event.clientY-this.drag.y;
        if (!this.drag.moved && Math.abs(dx) < 7) return;
        if (!this.drag.moved && Math.abs(dy)>Math.abs(dx)) { this.drag=null; return; }
        if (!this.drag.moved) {
          this.drag.moved=true; this.track.setPointerCapture?.(event.pointerId); this.track.classList.add('is-dragging');
        }
        event.preventDefault(); this.track.scrollLeft=this.drag.left-dx;
      },{signal});
      const end = () => {
        if (this.drag?.moved) { this.blockClickUntil=performance.now()+250; this.track.classList.remove('is-dragging'); }
        this.drag=null; this.update();
      };
      this.track.addEventListener('pointerup',end,{signal}); this.track.addEventListener('pointercancel',end,{signal});
      this.track.addEventListener('lostpointercapture',end,{signal});
      window.addEventListener('pointerup',end,{signal});
      this.track.addEventListener('click',(event) => { if (performance.now()<(this.blockClickUntil || 0)) {event.preventDefault();event.stopImmediatePropagation();} },{capture:true,signal});
      this.resize = new ResizeObserver(this.update); this.resize.observe(this.track);
      this.update();
    }
    isScrollable() { return this.track.scrollWidth > this.track.clientWidth+3 && getComputedStyle(this.track).overflowX !== 'visible'; }
    move(direction) {
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      this.track.scrollBy({left:direction*(rtl ? -1:1)*this.track.clientWidth*0.85,behavior:reduceMotion() ? 'auto':'smooth'});
    }
    update() {
      if (!this.track) return;
      const scrollable=this.isScrollable(); this.toggleAttribute('data-overflow',scrollable);
      const distance=Math.abs(this.track.scrollLeft), max=this.track.scrollWidth-this.track.clientWidth;
      if(this.prev){this.prev.disabled=!scrollable||distance<3;this.prev.hidden=!scrollable;}
      if(this.next){this.next.disabled=!scrollable||distance>=max-3;this.next.hidden=!scrollable;}
      this.track.tabIndex=scrollable ? 0:-1;
    }
    disconnectedCallback(){this.abort?.abort();this.resize?.disconnect();}
  });

  let lockCount=0, previousOverflow='';
  if (!customElements.get('ts-size-dialog')) customElements.define('ts-size-dialog',class extends HTMLElement {
    connectedCallback(){
      this.dialog=this.querySelector('dialog');this.opener=this.querySelector('[data-ts-dialog-open]');
      if(!this.dialog||!this.opener)return;
      this.abort=new AbortController();const signal=this.abort.signal;
      this.opener.addEventListener('click',()=>{
        if(this.dialog.open)return;
        this.dialog.showModal();
        if(lockCount++===0){previousOverflow=document.body.style.overflow;document.body.style.overflow='hidden';}
        this.locked=true;
        document.dispatchEvent(new CustomEvent('ts:track',{detail:{name:'size_guide_open',product_id:this.dataset.product}}));
      },{signal});
      this.querySelector('[data-ts-dialog-close]')?.addEventListener('click',()=>this.dialog.close(),{signal});
      this.dialog.addEventListener('click',(event)=>{
        if(event.target!==this.dialog)return;
        const bounds=this.dialog.getBoundingClientRect();
        if(event.clientX<bounds.left||event.clientX>bounds.right||event.clientY<bounds.top||event.clientY>bounds.bottom)this.dialog.close();
      },{signal});
      this.dialog.addEventListener('close',()=>{this.unlock();if(this.opener.isConnected)this.opener.focus({preventScroll:true});},{signal});
    }
    unlock(){if(this.locked){this.locked=false;lockCount=Math.max(0,lockCount-1);if(!lockCount)document.body.style.overflow=previousOverflow;}}
    disconnectedCallback(){this.dialog?.close();this.unlock();this.abort?.abort();}
  });

  if (!customElements.get('ts-card-colors')) customElements.define('ts-card-colors',class extends HTMLElement {
    connectedCallback(){
      this.abort=new AbortController();const signal=this.abort.signal;
      this.addEventListener('click',(event)=>{
        const button=event.target.closest('[data-ts-color-preview]');if(!button||!this.contains(button))return;
        event.preventDefault();
        const card=this.closest('.card-wrapper');if(!card)return;
        this.querySelectorAll('[data-ts-color-preview]').forEach((item)=>item.setAttribute('aria-pressed',item===button?'true':'false'));
        const image=card.querySelector('.card__media .media img');
        if(image&&button.dataset.image){image.src=button.dataset.image;image.srcset=button.dataset.srcset||'';image.alt=button.dataset.alt||image.alt;card.classList.add('ts-color-preview-active');}
        card.querySelectorAll('.card__heading a, a[id^="CardLink-"]').forEach((link)=>{link.href=button.href;});
        card.querySelectorAll('[data-product-url]').forEach((item)=>{item.dataset.productUrl=button.href;});
        const label=this.querySelector('[data-ts-color-label]');if(label)label.textContent=button.dataset.color;
      },{signal});
      this.addEventListener('keydown',(event)=>{if(event.key===' '&&event.target.matches('[data-ts-color-preview]')){event.preventDefault();event.target.click();}},{signal});
    }
    disconnectedCallback(){this.abort?.abort();}
  });

  if (!customElements.get('ts-shipping-progress')) customElements.define('ts-shipping-progress',class extends HTMLElement {
    connectedCallback(){
      this.rule={enabled:this.dataset.enabled==='true',threshold:Number(this.dataset.threshold),currency:this.dataset.currency,country:this.dataset.countries};
      this.render({total:Number(this.dataset.total),currency:this.dataset.cartCurrency,country:this.dataset.country,requiresShipping:this.dataset.requiresShipping==='true'});
      if(typeof subscribe==='function'&&typeof PUB_SUB_EVENTS!=='undefined')this.off=subscribe(PUB_SUB_EVENTS.cartUpdate,()=>this.refresh());
    }
    async refresh(){
      this.request?.abort();this.request=new AbortController();
      try{
        const response=await fetch(`${window.routes?.cart_url||'/cart'}.js`,{credentials:'same-origin',signal:this.request.signal,headers:{Accept:'application/json'}});
        if(!response.ok)throw new Error('Cart unavailable');
        const cart=await response.json();
        this.render({total:cart.total_price,currency:cart.currency,country:this.dataset.country,requiresShipping:cart.requires_shipping});
      }catch(error){if(error.name!=='AbortError')this.hidden=true;}
    }
    render(cart){
      const state=window.TSCommerce?.shippingState(this.rule,cart);this.hidden=!state?.visible;if(this.hidden)return;
      let amount='';try{amount=new Intl.NumberFormat(document.documentElement.lang||'en',{style:'currency',currency:cart.currency}).format(state.remaining/100);}catch(_){amount=`${cart.currency} ${(state.remaining/100).toFixed(2)}`;}
      const message=state.qualified?strings('shippingQualified','Your cart meets the configured free-shipping threshold.'):strings('shippingRemaining','Add [amount] more for free shipping.').replace('[amount]',amount);
      const label=this.querySelector('[data-ts-shipping-label]');if(label&&label.textContent!==message)label.textContent=message;
      const progress=this.querySelector('progress');if(progress)progress.value=state.progress;
    }
    disconnectedCallback(){this.off?.();this.request?.abort();}
  });

  // Namespaced custom events only. No checkout/purchase events are synthesized.
  const publishEvent=(name,payload)=>{
    if(!window.tsConfig?.eventsEnabled||!['amazon_click','size_guide_open','creator_cta_click','buy_now_click'].includes(name))return;
    const data=Object.fromEntries(Object.entries(payload).filter(([,value])=>value!==undefined&&value!==null&&value!==''));
    try{const result=window.Shopify?.analytics?.publish?.(`todaysunny:${name}`,data);result?.catch?.(()=>{});}catch(_){}
    // Optional GTM integration does not emit until analytics processing is explicitly allowed.
    if(window.tsConfig.dataLayerEnabled&&window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.()===true){
      window.dataLayer=window.dataLayer||[];window.dataLayer.push({event:`todaysunny_${name}`,...data});
    }
  };
  document.addEventListener('click',(event)=>{
    const link=event.target.closest('a[data-ts-event]');if(!link?.href)return;
    const name=link.dataset.tsEvent;
    if(name==='amazon_click'){
      const url=window.TSCommerce?.validAmazonURL(link.href);if(!url)return;
      const parsed=new URL(url),source=link.closest('ts-amazon-link');
      publishEvent(name,{product_id:source?.dataset.product,variant_id:source?.dataset.variant,destination_host:parsed.hostname,destination_path:parsed.pathname,placement:link.closest('quick-add-modal')?'quick_add':'product'});
    }else if(name==='creator_cta_click')publishEvent(name,{placement:link.closest('[id^="shopify-section-"]')?.id});
  });
  document.addEventListener('ts:buy-now',(event)=>{const info=event.target.closest('product-info');publishEvent('buy_now_click',{product_id:info?.dataset.productId,variant_id:event.detail?.variantId,quantity:event.detail?.quantity,placement:info?.dataset.originalSection?'quick_add':'product'});});
  document.addEventListener('ts:track',(event)=>{const {name,...data}=event.detail||{};publishEvent(name,data);});
})();
